/**
 * The `Trend` catalog component: periods on one scale, one of them lit.
 *
 * The design's second most-used visual — 31 instances (13p, 19b) — and the
 * one a line would draw wrongly. Five fiscal filings are five facts with
 * gaps between them, not a continuous quantity, so they are bars: a line
 * through them would assert that the value moved smoothly between two
 * annual reports.
 *
 * ONE SCALE, AND IT INCLUDES ZERO. The opposite of `Line`, deliberately.
 * A line is scaled to its own values because a zero baseline draws a week
 * of NEPSE closes as a flat line; a bar's LENGTH is the claim it makes, so
 * a bar chart that did not start at zero would make every claim wrong by a
 * constant. Both rules are the same rule — the geometry must not say
 * something the figures do not.
 *
 * THE LIT BAR IS NOT THE LARGEST. The server chose it: a `current` field on
 * the row, or the last period on the axis. It says which period the answer
 * is about, so it is drawn in ink and the rest in ink-soft — a weight
 * difference and not a colour one, because green and red belong to the
 * market and a fiscal year is neither good news nor bad.
 *
 * Negative values are why the baseline is computed rather than assumed: a
 * loss-making quarter's EPS is below zero and its bar has to hang under the
 * axis rather than be clipped to nothing.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import { captionLabel, cellNumber, type CellValue } from '@/features/mitra/lib/figures'
import { Fig, Label, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const TrendApi = {
  name: 'Trend',
  schema: z
    .object({
      bars: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/bars`, fixed by `to_messages`. */
export type BarPart = {
  id: string
  label: string
  value: CellValue
  lit: boolean
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const trendApi = TrendApi as unknown as ComponentApi

export const Trend = createComponentImplementation<ComponentApi>(
  trendApi,
  ({ props }: { props: { bars?: unknown; caption?: unknown } }) => (
    <TrendBars
      bars={listOf<BarPart>(props.bars)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The tallest a bar's body may draw, in px. The row is this plus its labels. */
const BAR_H = 78

/** The drawing, given the list the surface resolved. */
export function TrendBars({ bars, caption }: { bars: BarPart[]; caption: string | null }) {
  const values = bars.map((b) => cellNumber(b.value))
  const drawable = values.every((v) => Number.isFinite(v))
  if (bars.length === 0 || !drawable) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }

  // One scale for every bar, anchored at zero — see the note above. `span`
  // is never zero, so a set of identical values draws identical full-height
  // bars rather than dividing by nothing.
  const top = Math.max(0, ...values)
  const bottom = Math.min(0, ...values)
  const span = top - bottom || 1
  const zero = (top / span) * BAR_H

  return (
    <figure
      data-role="chart"
      data-component="trend"
      className="m-0 flex flex-col gap-2 px-3 pb-3 pt-3"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      {/* Each period owns its bar, figure and label. The whole plot pans as
          one when the rail is narrow, so no figure is silently ellipsized. */}
      <div
        data-role="trend-scroll"
        className={`${SCROLL_X} focus-visible:outline-2 focus-visible:outline-brand-deep`}
        tabIndex={0}
        aria-label={caption ?? 'Trend chart'}
      >
        <div className="flex min-w-full items-end gap-2 pb-1">
          {bars.map((b, i) => {
            const height = (Math.abs(values[i] ?? 0) / span) * BAR_H
            const below = (values[i] ?? 0) < 0
            return (
              <div key={b.id} className="flex min-w-max flex-1 flex-col gap-2 px-2">
                <div
                  data-role="bar"
                  data-lit={b.lit ? 'true' : 'false'}
                  className="relative flex flex-col justify-end"
                  style={{
                    height: `${String(BAR_H)}px`,
                    paddingTop: below ? `${String(zero)}px` : undefined,
                    paddingBottom: below ? undefined : `${String(BAR_H - zero)}px`,
                  }}
                >
                  <span
                    className={`block w-full ${b.lit ? 'bg-ink' : 'bg-ink-soft/40'}`}
                    style={{ height: `${String(Math.max(height, 1))}px` }}
                  />
                </div>
                <span
                  data-role="bar-value"
                  className={`whitespace-nowrap text-center text-body-s tabular-nums ${
                    b.lit ? 'font-bold text-ink' : 'text-ink-soft'
                  }`}
                >
                  <Fig cell={b.value} quietUnit />
                </span>
                <span
                  data-role="bar-label"
                  className={`whitespace-nowrap text-center font-mono text-body-xs tabular-nums ${
                    b.lit ? 'text-ink' : 'text-ink-soft'
                  }`}
                >
                  {b.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </figure>
  )
}
